import React, { useState, useEffect, useRef } from "react";
import { authFetch } from "../api";
import Header from "../components/Header";
import NeighborhoodSelect from "../components/NeighborhoodSelect";
import SavedSearchBar from "../components/SavedSearchBar";
import ListingFilterPanel from "../components/filters/ListingFilterPanel";
import useListingFilters from "../components/filters/useListingFilters";
import { inputStyle, labelStyle } from "../components/filters/styles";
import { getListingTypeLabel } from "../utils/listingType";
import { fromStored, toParams } from "../components/filters/filterSpec";

// 5 visual columns fed by 4 server "groups". In Review + Reviewed share one grouped query
// (split client-side); each column still shows its own count via status_counts. Retired is
// windowed server-side to the last 14 days.
// `aiScope` is what "Refresh AI" asks the staleness endpoint to look at — the column, not the
// group. In Review and Reviewed share the in_review group, so each targets itself by status;
// sending the group would make either button re-run both columns. Retired has no aiScope and
// so no button: it is a 14-day window on dropped listings, and AI calls on dead deals are
// spend for nothing.
const COLUMNS = [
  { key: "TO_REVIEW", label: "New", color: "#e3f2fd", group: "new", pager: "new", aiScope: { group: "new" } },
  { key: "IN_REVIEW", label: "In Review", color: "#fff3e0", group: "in_review", status: "IN_REVIEW", pager: "in_review", aiScope: { status: "IN_REVIEW" } },
  { key: "REVIEWED", label: "Reviewed", color: "#e8f5e9", group: "in_review", status: "REVIEWED", aiScope: { status: "REVIEWED" } },
  { key: "MIS", label: "MIS", color: "#fce4ec", group: "mis", pager: "mis", aiScope: { group: "mis" } },
  { key: "RETIRED", label: "Retired (14d)", color: "#f5f5f5", group: "retired", pager: "retired" },
];
const GROUP_KEYS = ["new", "in_review", "mis", "retired"];
const PAGE_SIZE = 25;
const EMPTY_GROUP = { rows: [], total: 0, status_counts: {}, page: 0 };

export default function SaleReviewsPage() {
  const [groups, setGroups] = useState({ new: { ...EMPTY_GROUP }, in_review: { ...EMPTY_GROUP }, mis: { ...EMPTY_GROUP }, retired: { ...EMPTY_GROUP } });
  const [loading, setLoading] = useState(true);
  const [paging, setPaging] = useState(null); // group key currently paging (for button state)
  const [draggedItem, setDraggedItem] = useState(null);

  // "Refresh AI": re-runs AI only on listings whose prediction is dated (detected
  // server-side), so fresh ones never spend an AI call. Progress + cancel.
  // Carries the column key because every column but Retired has the button now, and only the
  // one actually running should show progress -- the others disable while it does.
  const [aiRefresh, setAiRefresh] = useState(null); // null | { col, label, done, total }
  const cancelAiRef = useRef(false);

  // Modal state for MIS/Retired transitions
  const [showModal, setShowModal] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [statusComment, setStatusComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Filter draft state (staged — only sent to the server on "Search"). Everything in the
  // shared spec lives in the hook; address, hoods, property type and sort are this board's own.
  const filters = useListingFilters();
  const [searchAddress, setSearchAddress] = useState("");
  const [propertyType, setPropertyType] = useState("all");
  // "" = every status the board carries; "recent" = Temp Off Market only, and only a spell
  // that started in the last 30 days.
  const [tomFilter, setTomFilter] = useState("");
  const [sortBy, setSortBy] = useState("date_desc");
  const [selectedNeighborhoods, setSelectedNeighborhoods] = useState([]);
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [defaultNeighborhoods, setDefaultNeighborhoods] = useState([]);

  const [applied, setApplied] = useState(null); // snapshot of filters behind the current results
  const [neighborhoodsLoaded, setNeighborhoodsLoaded] = useState(false);

  useEffect(() => { loadNeighborhoods(); }, []);
  useEffect(() => {
    if (neighborhoodsLoaded) loadAll(collectFilters());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [neighborhoodsLoaded]);

  const loadNeighborhoods = async () => {
    try {
      const data = await authFetch("/sale-reviews/neighborhoods");
      setNeighborhoods(data.neighborhoods);
      let defaultIds = [];
      if (data.default_neighborhoods && data.default_neighborhoods.length > 0) {
        defaultIds = data.default_neighborhoods.map((item) => {
          if (typeof item === "number") return item;
          if (typeof item === "string" && /^\d+$/.test(item)) return parseInt(item, 10);
          const found = data.neighborhoods.find((n) => n.name === item);
          return found ? found.id : null;
        }).filter((id) => id !== null);
      }
      setDefaultNeighborhoods(defaultIds);
      if (defaultIds.length > 0) setSelectedNeighborhoods(defaultIds);
      setNeighborhoodsLoaded(true);
    } catch (error) {
      console.error("Error loading neighborhoods:", error);
      setNeighborhoodsLoaded(true);
    }
  };

  // One object holding both halves of the filter: the board's own controls, and the shared
  // spec's values. It is also exactly what a saved search stores.
  const collectFilters = () => ({
    search: searchAddress,
    neighborhood_ids: selectedNeighborhoods,
    property_type: propertyType,
    tom_filter: tomFilter,
    sort_by: sortBy,
    ...filters.toStored(),
  });

  // `scope` is either a group key (the four board queries) or {group} / {status} — In Review
  // and Reviewed share the in_review group, so targeting one of them alone needs `status`.
  const buildParams = (f, scope, pageNum) => {
    const p = new URLSearchParams();
    const { group, status } = typeof scope === "string" ? { group: scope } : scope;
    if (group) p.append("group", group);
    if (status) p.append("status", status);
    // Search and neighborhood are independent constraints (logical AND): if a hood is
    // checked, always send it so an address search stays scoped to the selected hoods.
    if (f.search) p.append("search", f.search);
    if (f.neighborhood_ids && f.neighborhood_ids.length > 0) p.append("neighborhood_ids", f.neighborhood_ids.join(","));
    if (f.property_type && f.property_type !== "all") p.append("property_type", f.property_type);
    if (f.tom_filter) p.append("tom_filter", f.tom_filter);
    // Everything in the shared spec serialises through one function, so the pipeline and the
    // Listings tab cannot spell the same filter two different ways.
    toParams(fromStored(f), p);
    p.append("sort_by", f.sort_by || "date_desc");
    p.append("limit", PAGE_SIZE);
    p.append("offset", pageNum * PAGE_SIZE);
    return p;
  };

  const fetchGroup = async (f, group, pageNum) => {
    try {
      const data = await authFetch(`/sale-reviews?${buildParams(f, group, pageNum).toString()}`);
      setGroups((g) => ({ ...g, [group]: {
        rows: data.reviews || [], total: data.total_count || 0,
        status_counts: data.status_counts || {}, page: pageNum,
      } }));
    } catch (e) {
      console.error("group fetch failed:", group, e);
    }
  };

  const loadAll = async (f) => {
    setLoading(true);
    try {
      await Promise.all(GROUP_KEYS.map((g) => fetchGroup(f, g, 0)));
      setApplied(f);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => loadAll(collectFilters());

  const pageGroup = async (group, delta) => {
    const next = Math.max(0, (groups[group].page || 0) + delta);
    setPaging(group);
    await fetchGroup(applied || collectFilters(), group, next);
    setPaging(null);
  };

  // After a status change, refetch every group at its current page so the card moves to the
  // right column and all counts update.
  const refreshAll = () => {
    const f = applied || collectFilters();
    GROUP_KEYS.forEach((g) => fetchGroup(f, g, groups[g].page || 0));
  };

  // Refresh AI for ONE column under the CURRENT filter. Detection is server-side
  // (/sale-reviews/ai-staleness) so we only spend AI calls on DATED predictions; fresh ones
  // are never touched. Then we loop the existing force re-run per stale listing, with progress.
  //
  // Every column but Retired has this. Retired is excluded on purpose: those listings are out
  // of the pipeline, the column is a 14-day window on what was dropped, and spending AI calls
  // to keep dead deals current is the one place this button costs money for nothing.
  const handleRefreshAI = async (col) => {
    if (aiRefresh) return; // one at a time — these are AI calls, not page loads
    const f = applied || collectFilters();
    let info;
    try {
      // Reuses the board's exact filter (the extra paging params are ignored server-side), so
      // "refresh this column" always means the listings actually on screen.
      const params = buildParams(f, col.aiScope, 0);
      info = await authFetch(`/sale-reviews/ai-staleness?${params.toString()}`);
    } catch (e) {
      console.error("staleness check failed:", e);
      alert("Could not check which predictions are dated. Try again.");
      return;
    }
    const ids = info.stale_ids || [];
    if (!ids.length) {
      alert(`All AI predictions in the ${col.label} column are current — nothing to refresh (${info.fresh} fresh).`);
      return;
    }
    // A backlog this size is a backfill, not drift — say so rather than inviting someone to
    // click through it a few hundred at a time.
    const capNote = info.capped
      ? `\n\nCapped at ${ids.length} this run — ${info.stale} are dated in total.`
        + (info.stale > 1000
            ? ` That is a backfill rather than drift: better run calculate_predictions.py --force on the cron box than click through it here.`
            : ` Run again to continue.`)
      : "";
    if (!window.confirm(
      `Re-run AI for ${ids.length} dated listing${ids.length === 1 ? "" : "s"} in the ${col.label} column?\n` +
      `${info.fresh} already-current listing${info.fresh === 1 ? "" : "s"} will be skipped (no AI call).${capNote}`
    )) return;

    cancelAiRef.current = false;
    setAiRefresh({ col: col.key, label: col.label, done: 0, total: ids.length });
    let done = 0;
    for (const id of ids) {
      if (cancelAiRef.current) break;
      try {
        await authFetch(`/predict/sale/${id}/ai-analysis?force=true`, { method: "POST" });
      } catch (e) {
        console.error(`AI refresh failed for listing ${id}:`, e);
      }
      done += 1;
      setAiRefresh({ col: col.key, label: col.label, done, total: ids.length });
    }
    const canceled = cancelAiRef.current;
    setAiRefresh(null);
    refreshAll();
    alert(`${canceled ? "Stopped" : "Done"} — refreshed AI for ${done} of ${ids.length} listing${ids.length === 1 ? "" : "s"} in ${col.label}.`);
  };

  // Apply a saved search (see components/SavedSearchBar.jsx). The stored payload holds numbers
  // where the board's <select>s hold strings, so each field is put back in the shape its
  // control expects; the same object is handed straight to loadAll so the board doesn't wait
  // on the setState round-trip.
  const applySavedSearch = (saved) => {
    const f = {
      search: saved.search == null ? "" : String(saved.search),
      neighborhood_ids: Array.isArray(saved.neighborhood_ids) ? saved.neighborhood_ids : [],
      property_type: saved.property_type || "all",
      tom_filter: saved.tom_filter || "",
      sort_by: saved.sort_by || "date_desc",
      ...saved,
    };
    setSearchAddress(f.search);
    setSelectedNeighborhoods(f.neighborhood_ids);
    setPropertyType(f.property_type);
    setTomFilter(f.tom_filter || "");
    setSortBy(f.sort_by);
    // fromStored() puts every shared-spec value back in the shape its control expects — the
    // stored payload holds numbers where the controls hold strings.
    filters.load(saved);
    // Hand loadAll the object directly rather than waiting a render for setState to land.
    loadAll(f);
  };

  const handleClearFilters = () => {
    setSearchAddress("");
    setPropertyType("all");
    setTomFilter("");
    setSortBy("date_desc");
    setSelectedNeighborhoods(defaultNeighborhoods.length > 0 ? defaultNeighborhoods : []);
    filters.load({});
  };

  // ── Drag & drop status changes ──
  const handleDragStart = (e, review) => { setDraggedItem(review); e.dataTransfer.effectAllowed = "move"; };
  const handleDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };

  const handleDrop = async (e, newStatus) => {
    e.preventDefault();
    if (!draggedItem || draggedItem.status === newStatus) { setDraggedItem(null); return; }
    if (newStatus === "MIS" || newStatus === "RETIRED") {
      setPendingStatus(newStatus);
      setShowModal(true);
      return; // keep draggedItem until modal resolves
    }
    await updateStatus(newStatus, `Status changed from ${draggedItem.status} to ${newStatus}`);
  };

  const updateStatus = async (newStatus, reason) => {
    if (!draggedItem) return;
    const target = draggedItem;
    try {
      await authFetch(`/sale-reviews/${target.id}/status`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus, reason }),
      });
      refreshAll();
    } catch (error) {
      console.error("Error updating status:", error);
      alert("Failed to update status");
    } finally {
      setDraggedItem(null);
    }
  };

  const handleModalConfirm = async () => {
    if (!statusComment.trim()) { alert("Please provide a reason for this status change."); return; }
    setSubmitting(true);
    await updateStatus(pendingStatus, statusComment);
    setSubmitting(false);
    setShowModal(false); setStatusComment(""); setPendingStatus(null);
  };

  const handleModalCancel = () => {
    setShowModal(false); setStatusComment(""); setPendingStatus(null); setDraggedItem(null);
  };

  const rowsFor = (col) => col.status ? (groups[col.group].rows || []).filter((r) => r.status === col.status) : (groups[col.group].rows || []);
  const countFor = (col) => col.status ? (groups[col.group].status_counts[col.status] || 0) : (groups[col.group].total || 0);

  // ── Styles / helpers ──
  const pageStyle = { minHeight: "100vh", backgroundColor: "#f5f5f5" };
  const containerStyle = { padding: "20px", maxWidth: "1800px", margin: "0 auto" };
  const boardStyle = { display: "grid", gridTemplateColumns: `repeat(${COLUMNS.length}, 1fr)`, gap: "20px", marginTop: "20px" };
  const columnStyle = { backgroundColor: "#fff", borderRadius: "8px", padding: "15px", minHeight: "500px", boxShadow: "0 2px 4px rgba(0,0,0,0.1)", display: "flex", flexDirection: "column" };
  const columnHeaderStyle = (color) => ({ fontSize: "18px", fontWeight: "600", marginBottom: "15px", padding: "10px", backgroundColor: color, borderRadius: "4px", display: "flex", justifyContent: "space-between", alignItems: "center" });
  const cardStyle = { backgroundColor: "#fff", border: "1px solid #e0e0e0", borderRadius: "6px", padding: "12px", marginBottom: "10px", cursor: "grab", transition: "all 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" };
  const cardHoverStyle = { transform: "translateY(-2px)", boxShadow: "0 4px 8px rgba(0,0,0,0.15)" };
  const addressStyle = { fontSize: "14px", fontWeight: "600", marginBottom: "8px", color: "#333" };
  const detailsStyle = { fontSize: "12px", color: "#666", marginBottom: "4px" };
  const badgeStyle = { backgroundColor: "#e3f2fd", color: "#1976d2", padding: "2px 8px", borderRadius: "12px", fontSize: "11px", fontWeight: "500" };

  const getPriceStyle = (priceChange) => {
    let color = "#000";
    if (priceChange && priceChange < 0) color = "#2e7d32";
    else if (priceChange && priceChange > 0) color = "#d32f2f";
    return { fontSize: "16px", fontWeight: "bold", color, marginTop: "8px" };
  };
  const formatPriceChange = (change, pct) => {
    if (!change || change === 0) return null;
    const sign = change > 0 ? "+" : "-";
    const absChange = Math.abs(change);
    const formattedChange = absChange >= 1000 ? `${sign}$${(absChange / 1000).toFixed(0)}K` : `${sign}$${absChange.toLocaleString()}`;
    const formattedPct = pct ? ` (${sign}${Math.abs(pct).toFixed(1)}%)` : "";
    return `${formattedChange}${formattedPct}`;
  };

  return (
    <div style={pageStyle}>
      <Header showLogout={true} />
      <div style={containerStyle}>
        {/* Filter Panel — the shared component (components/filters/). `leading` carries the
            four controls that belong to this board rather than the shared spec; `header` is
            the saved-search bar, which only the pipeline has. */}
        <ListingFilterPanel
          scope="pipeline"
          values={filters.values}
          set={filters.set}
          onSearch={handleSearch}
          onReset={handleClearFilters}
          loading={loading}
          header={<SavedSearchBar scope="sale_reviews" currentFilters={collectFilters()} onLoad={applySavedSearch} />}
          leading={
            <>
              <NeighborhoodSelect
                neighborhoods={neighborhoods}
                selected={selectedNeighborhoods}
                onChange={setSelectedNeighborhoods}
                defaults={defaultNeighborhoods}
                placeholder="All Neighborhoods"
                gridColumn="span 2"
              />
              <div>
                <label style={labelStyle}>Address (use # for unit)</label>
                <input type="text" placeholder="Search by address or listing ID..." value={searchAddress}
                  onChange={(e) => setSearchAddress(e.target.value)} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Property Type</label>
                <select value={propertyType} onChange={(e) => setPropertyType(e.target.value)}
                  style={{ ...inputStyle, backgroundColor: "white" }}>
                  <option value="all">All Properties</option>
                  <option value="condo">Condo</option>
                  <option value="coop">Co-op</option>
                </select>
              </div>
              {/* Temp Off Market is already in the board's population — ~1,370 of them sit in
                  the New column — so the useful question is not "show me TOM" but "show me
                  the ones pulled recently", which are the ones that come back. */}
              <div>
                <label style={labelStyle}>Listing Status</label>
                <select value={tomFilter} onChange={(e) => setTomFilter(e.target.value)}
                  style={{ ...inputStyle, backgroundColor: "white" }}>
                  <option value="">All Statuses</option>
                  <option value="recent">Temp Off Market — 30 days or less</option>
                </select>
              </div>
            </>
          }
          trailing={
            <div>
              <label style={labelStyle}>Sort By</label>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}
                style={{ ...inputStyle, backgroundColor: "white" }}>
                <option value="date_desc">Newest First</option>
                <option value="date_asc">Oldest First</option>
                <option value="cap_desc">Cap Rate (High to Low)</option>
                <option value="cap_asc">Cap Rate (Low to High)</option>
                <option value="price_asc">Price (Low to High)</option>
                <option value="price_desc">Price (High to Low)</option>
                <option value="dom_asc">DOM (Low to High)</option>
                <option value="dom_desc">DOM (High to Low)</option>
              </select>
            </div>
          }
        />

        {/* The board is what loads — NOT the page.
            This used to be an early `if (loading) return <Loading/>`, which threw away the
            entire tree on every search and remounted the filter panel from scratch. Local
            state there went with it: the saved-search dropdown snapped back to "— none —"
            straight after running the search it had just loaded, and the "More filters"
            section collapsed. Keeping the panel mounted fixes both. */}
        {loading ? (
          <div style={{ textAlign: "center", marginTop: "50px", color: "#666" }}>
            Loading reviews...
          </div>
        ) : (
        <div style={boardStyle}>
          {COLUMNS.map((col) => {
            const rows = rowsFor(col);
            const count = countFor(col);
            const grp = col.pager ? groups[col.pager] : null;
            const totalPages = grp ? Math.max(1, Math.ceil(grp.total / PAGE_SIZE)) : 1;
            return (
              <div key={col.key} style={columnStyle} onDragOver={handleDragOver} onDrop={(e) => handleDrop(e, col.key)}>
                <div style={columnHeaderStyle(col.color)}>
                  <span>{col.label}</span>
                  <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    {col.aiScope && (
                      aiRefresh?.col === col.key ? (
                        <span style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "11px", fontWeight: 600, color: "#1565c0" }}>
                          <span>AI {aiRefresh.done}/{aiRefresh.total}…</span>
                          <button onClick={() => { cancelAiRef.current = true; }} title="Stop after the current listing"
                            style={{ fontSize: "10px", fontWeight: 700, padding: "2px 6px", border: "1px solid #ef9a9a", borderRadius: "4px", background: "#ffebee", color: "#c62828", cursor: "pointer" }}>
                            Stop
                          </button>
                        </span>
                      ) : (
                        <button onClick={() => handleRefreshAI(col)} disabled={!!aiRefresh}
                          title={aiRefresh
                            ? `Busy refreshing ${aiRefresh.label} — one column at a time.`
                            : "Re-run AI for listings in this column whose prediction is dated (old scoring version or comps changed since). Fresh ones are skipped — no wasted AI calls."}
                          style={{ fontSize: "11px", fontWeight: 600, padding: "3px 9px", border: `1px solid ${aiRefresh ? "#ddd" : "#90caf9"}`, borderRadius: "12px", background: "#fff", color: aiRefresh ? "#aaa" : "#1565c0", cursor: aiRefresh ? "not-allowed" : "pointer", whiteSpace: "nowrap" }}>
                          ↻ Refresh AI
                        </button>
                      )
                    )}
                    <span style={badgeStyle}>{count.toLocaleString()}</span>
                  </span>
                </div>

                <div style={{ flex: 1 }}>
                  {rows.map((review) => (
                    <div key={review.id} style={cardStyle} draggable
                      onDragStart={(e) => handleDragStart(e, review)}
                      onMouseEnter={(e) => Object.assign(e.currentTarget.style, cardHoverStyle)}
                      onMouseLeave={(e) => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.1)"; }}>
                      <div style={addressStyle}>
                        {review.property.address}{review.property.unit_number && ` #${review.property.unit_number}`}
                        <span onClick={(e) => {
                          e.stopPropagation();
                          const addr = (review.property.address || "").replace(/\s+/g, " ").trim();
                          const unit = (review.property.unit_number || "").trim();
                          navigator.clipboard?.writeText(`${addr}${unit ? ` #${unit}` : ""}`);
                        }} title="Copy address" style={{ cursor: "pointer", marginLeft: 6, fontSize: 12, opacity: 0.55 }}>📋</span>
                        {review.verified && (
                          <span title="Data verified" style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: "#2e7d32", background: "#e8f5e9", border: "1px solid #a5d6a7", borderRadius: 4, padding: "1px 5px", verticalAlign: "middle", whiteSpace: "nowrap" }}>✓ Verified</span>
                        )}
                        {(() => {
                          const lt = getListingTypeLabel(review.property.listing_type_code);
                          return lt && (
                            <span title={lt.label} style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: lt.color, background: lt.bg, border: `1px solid ${lt.border}`, borderRadius: 4, padding: "1px 5px", verticalAlign: "middle", whiteSpace: "nowrap" }}>{lt.short}</span>
                          );
                        })()}
                        {review.property.is_sponsor_unit && (
                          <span title="Sponsor unit" style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: "#6a1b9a", background: "#f3e5f5", border: "1px solid #ce93d8", borderRadius: 4, padding: "1px 5px", verticalAlign: "middle", whiteSpace: "nowrap" }}>Sponsor</span>
                        )}
                        {review.property.development_type && (
                          <span title={`New development: ${review.property.development_type}`} style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: "#00695c", background: "#e0f2f1", border: "1px solid #80cbc4", borderRadius: 4, padding: "1px 5px", verticalAlign: "middle", whiteSpace: "nowrap" }}>New Dev</span>
                        )}
                        {/* The age is the point, not the status: TOM listings are mixed in
                            with active ones everywhere on this board, and "pulled 4 days ago"
                            and "pulled 7 months ago" are different deals. It is also what
                            makes the filter above checkable. */}
                        {review.property.listing_status === "TEMP OFF MKT" && (() => {
                          const days = review.property.tom_since
                            ? Math.max(0, Math.floor((Date.now() - new Date(review.property.tom_since)) / 86400000))
                            : null;
                          const fresh = days != null && days <= 30;
                          return (
                            <span
                              title={days == null
                                ? "Temporarily off market — no date for when the spell began"
                                : `Temporarily off market since ${new Date(review.property.tom_since).toLocaleDateString()} (${days} days)`}
                              style={{ marginLeft: 6, fontSize: 10, fontWeight: 700,
                                       color: fresh ? "#e65100" : "#777",
                                       background: fresh ? "#fff3e0" : "#f0f0f0",
                                       border: `1px solid ${fresh ? "#ffb74d" : "#ddd"}`,
                                       borderRadius: 4, padding: "1px 5px", verticalAlign: "middle", whiteSpace: "nowrap" }}>
                              TOM{days != null ? ` ${days}d` : ""}
                            </span>
                          );
                        })()}
                      </div>
                      {review.property.property_type && (
                        <span style={{ display: "inline-block", fontSize: "11px", fontWeight: 600, color: "#555", border: "1px solid #ddd", borderRadius: "4px", padding: "1px 6px", marginBottom: "4px" }}>
                          {["COOP", "CONDP"].includes((review.property.property_type || "").toUpperCase()) ? "Co-op"
                            : (review.property.property_type || "").toUpperCase() === "CONDO" ? "Condo" : review.property.property_type}
                        </span>
                      )}
                      <div style={detailsStyle}>{review.property.neighborhood}, {review.property.borough}</div>
                      {review.property.listed_at && (
                        <div style={detailsStyle}>Listed: {new Date(review.property.listed_at).toLocaleDateString()}</div>
                      )}
                      <div style={detailsStyle}>
                        {review.property.bedrooms !== null ? review.property.bedrooms : "?"} bed •{" "}
                        {review.property.bathrooms || "?"} bath • {review.property.sqft || "?"} sqft
                      </div>
                      {review.property.price && (
                        <div style={getPriceStyle(review.property.price_change)}>
                          ${review.property.price.toLocaleString()}
                          {formatPriceChange(review.property.price_change, review.property.price_change_pct) && (
                            <span style={{ fontSize: "12px", fontWeight: "500", marginLeft: "6px" }}>
                              {formatPriceChange(review.property.price_change, review.property.price_change_pct)}
                            </span>
                          )}
                        </div>
                      )}
                      {(review.property.ai_cap_rate != null || review.property.cap_rate != null || review.property.neighborhood_avg_cap_rate != null) && (
                        <div style={{ fontSize: "14px", fontWeight: "600", marginTop: "4px" }}>
                          {review.property.ai_cap_rate != null && (
                            <span style={{ color: "#1976d2" }}>AI Cap: {review.property.ai_cap_rate.toFixed(2)}%</span>
                          )}
                          {review.property.cap_rate != null && (
                            <span style={{ color: "#607d8b", marginLeft: review.property.ai_cap_rate != null ? "10px" : 0 }}>
                              ML Cap: {review.property.cap_rate.toFixed(2)}%
                            </span>
                          )}
                          {/* Neighborhood benchmark shows regardless of whether this listing
                              has its own prediction — it's a property of the hood, not the listing. */}
                          {review.property.neighborhood_avg_cap_rate != null && (
                            <span style={{ fontSize: "12px", fontWeight: "400", color: "#666", marginLeft: (review.property.ai_cap_rate != null || review.property.cap_rate != null) ? "8px" : 0 }}>
                              (Nbhd avg: {review.property.neighborhood_avg_cap_rate.toFixed(2)}%)
                            </span>
                          )}
                        </div>
                      )}
                      {review.property.below_building_sale && (
                        <div style={{ fontSize: "12px", fontWeight: "700", color: "#e65100", backgroundColor: "#fff3e0", padding: "3px 8px", borderRadius: "4px", marginTop: "6px", border: "1px solid #ffcc80", display: "inline-block" }}
                          title={`Match: ${review.property.below_building_sale.match_type === "same_line" ? "Same line" : "Same bed/bath/sqft"} | Sold: $${review.property.below_building_sale.sold_price?.toLocaleString()} (${review.property.below_building_sale.sold_date?.slice(0, 7) || "?"})`}>
                          {review.property.below_building_sale.pct_below}% below sold #{review.property.below_building_sale.sold_unit}
                        </div>
                      )}
                      {review.status_reason && (
                        <div style={{ fontSize: "12px", color: "#555", marginTop: "6px", padding: "6px 8px", backgroundColor: "#f5f5f5", borderRadius: "4px", borderLeft: "3px solid #1976d2", fontStyle: "italic" }}>
                          {review.status_reason}
                        </div>
                      )}
                      <div style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
                        <button style={{ padding: "4px 8px", fontSize: "11px", backgroundColor: "#1976d2", color: "#fff", border: "none", borderRadius: "4px", cursor: "pointer" }}
                          onClick={(e) => { e.stopPropagation(); window.open(`/predict/${review.sale_listing_id}`, "_blank"); }}>View Analysis</button>
                        <button style={{ padding: "4px 8px", fontSize: "11px", backgroundColor: "#7b1fa2", color: "#fff", border: "none", borderRadius: "4px", cursor: "pointer" }}
                          onClick={(e) => { e.stopPropagation(); window.open(`/comps/${review.sale_listing_id}`, "_blank"); }}>Comps</button>
                        {review.property.listing_url && (
                          <button style={{ padding: "4px 8px", fontSize: "11px", backgroundColor: "#2e7d32", color: "#fff", border: "none", borderRadius: "4px", cursor: "pointer" }}
                            onClick={(e) => { e.stopPropagation(); window.open(review.property.listing_url, "_blank"); }}>Web Listing</button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Per-column Prev/Next (only the group's primary column has the pager) */}
                {col.pager && grp.total > PAGE_SIZE && (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "6px", marginTop: "10px", paddingTop: "10px", borderTop: "1px solid #eee" }}>
                    <button disabled={grp.page <= 0 || paging === col.pager} onClick={() => pageGroup(col.pager, -1)}
                      style={pagerBtn(grp.page <= 0 || paging === col.pager)}>‹</button>
                    <span style={{ fontSize: "11px", color: "#666" }}>{grp.page + 1}/{totalPages}</span>
                    <button disabled={grp.page >= totalPages - 1 || paging === col.pager} onClick={() => pageGroup(col.pager, 1)}
                      style={pagerBtn(grp.page >= totalPages - 1 || paging === col.pager)}>›</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        )}
      </div>

      {/* Modal for MIS/Retired comment */}
      {showModal && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10000 }}>
          <div style={{ backgroundColor: "#fff", borderRadius: "8px", padding: "24px", maxWidth: "500px", width: "90%", boxShadow: "0 4px 20px rgba(0,0,0,0.3)" }}>
            <h3 style={{ margin: "0 0 16px 0", fontSize: "18px", color: "#333" }}>
              {pendingStatus === "MIS" ? "Mark as MIS" : "Mark as Retired"}
            </h3>
            <p style={{ margin: "0 0 16px 0", fontSize: "14px", color: "#666" }}>
              Please provide a reason for this status change (required):
            </p>
            <textarea value={statusComment} onChange={(e) => setStatusComment(e.target.value)} placeholder="Enter reason... (Enter to confirm, Shift+Enter for new line)" autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  if (statusComment.trim() && !submitting) handleModalConfirm();
                }
              }}
              style={{ width: "100%", minHeight: "100px", padding: "12px", fontSize: "14px", border: "1px solid #ddd", borderRadius: "4px", fontFamily: "inherit", resize: "vertical", boxSizing: "border-box" }} />
            <div style={{ display: "flex", gap: "12px", marginTop: "20px", justifyContent: "flex-end" }}>
              <button onClick={handleModalCancel} disabled={submitting}
                style={{ padding: "10px 20px", fontSize: "14px", fontWeight: "600", backgroundColor: "#f5f5f5", color: "#333", border: "1px solid #ddd", borderRadius: "6px", cursor: submitting ? "not-allowed" : "pointer", opacity: submitting ? 0.6 : 1 }}>
                Cancel
              </button>
              <button onClick={handleModalConfirm} disabled={!statusComment.trim() || submitting}
                style={{ padding: "10px 20px", fontSize: "14px", fontWeight: "600", backgroundColor: pendingStatus === "MIS" ? "#4caf50" : "#ff9800", color: "#fff", border: "none", borderRadius: "6px", cursor: (!statusComment.trim() || submitting) ? "not-allowed" : "pointer", opacity: (!statusComment.trim() || submitting) ? 0.5 : 1 }}>
                {submitting ? "Updating..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const pagerBtn = (disabled) => ({
  padding: "3px 12px", fontSize: "14px", border: "1px solid #ccc", borderRadius: "4px",
  background: disabled ? "#f5f5f5" : "#fff", color: disabled ? "#bbb" : "#333",
  cursor: disabled ? "not-allowed" : "pointer",
});
